/**
 * Aquatic and ambient mobs (Bedrock numbers):
 *  - squid: 10 HP, 0.95 x 0.95, drifts through water, squirts ink and bolts when
 *    hurt; drops 1-3 ink sacs, 1-3 XP. Needs water: suffocates (1 dmg / s) after 15 s on land.
 *  - cod: 3 HP, 0.5 x 0.3, school of 3-9; drops 1 raw cod (cooked when burning), 1-3 XP.
 *  - salmon: 3 HP, 0.7 x 0.4, school of 1-5, rivers and cold oceans; drops 1 raw salmon.
 *  - bat: 6 HP, 0.5 x 0.9, flies erratically in dark caves, hangs from ceilings by day;
 *    no drops, no damage.
 * Water mobs steer with plain velocity inside water cells (they turn back at the shore);
 * the PATHING swimmer navigator is not used because open water needs no paths.
 */

import { IS_WATERY } from '../../blocks';
import { I } from '../../game/itemIds';
import { stackOf, type MobType, type Stack } from '../../game/types';
import { Mob, type MobCtx } from '../mob';
import { newSteer, steerFlying } from '../nav/navigator';

export const AQUATIC_TYPES: ReadonlySet<MobType> = new Set<MobType>(['squid', 'cod', 'salmon']);
export const AMBIENT_TYPES: ReadonlySet<MobType> = new Set<MobType>(['squid', 'cod', 'salmon', 'bat', 'dolphin', 'axolotl']);
export const AIR_OUT_OF_WATER = 300;

const roll = (rnd: () => number, lo: number, hi: number): number => lo + Math.floor(rnd() * (hi - lo + 1));
const STEER = newSteer();

abstract class WaterMob extends Mob {
  protected airOut = 0;
  protected tx = 0; protected ty = 0; protected tz = 0;
  protected retarget = 0;
  protected panic = 0;
  /** Cruise speed in blocks / tick. */
  protected abstract cruise: number;

  protected override floats(): boolean { return false; }
  override onHurtBy(): void { this.panic = 60; this.retarget = 0; }

  protected waterAt(x: number, y: number, z: number): boolean {
    return IS_WATERY[this.w.getBlock(Math.floor(x), Math.floor(y), Math.floor(z))] === 1;
  }

  private pick(ctx: MobCtx, far: number): void {
    const r = ctx.host.rnd;
    for (let i = 0; i < 10; i++) {
      const x = this.x + (r() * 2 - 1) * far, y = this.y + (r() * 2 - 1) * far * 0.4, z = this.z + (r() * 2 - 1) * far;
      if (this.waterAt(x, y, z) && this.waterAt(x, y + 0.5, z)) { this.tx = x; this.ty = y; this.tz = z; this.retarget = 40 + Math.floor(r() * 60); return; }
    }
    this.retarget = 20;
    this.tx = this.x; this.ty = this.y; this.tz = this.z;
  }

  protected swim(ctx: MobCtx): void {
    if (this.panic > 0) this.panic--;
    if (!this.inWater) {
      // beached: flop around and slowly suffocate
      this.noFallDamage = true;
      if (this.onGround && ctx.host.rnd() < 0.15) { this.vy = 0.25; this.vx = (ctx.host.rnd() - 0.5) * 0.2; this.vz = (ctx.host.rnd() - 0.5) * 0.2; }
      if (++this.airOut > AIR_OUT_OF_WATER && this.airOut % 20 === 0) this.hurt(1);
      return;
    }
    this.airOut = 0;
    if (--this.retarget <= 0 || (this.tx - this.x) ** 2 + (this.ty - this.y) ** 2 + (this.tz - this.z) ** 2 < 1) this.pick(ctx, this.panic > 0 ? 10 : 6);
    // turn back before leaving the water
    const ax = this.x + this.vx * 6, az = this.z + this.vz * 6;
    if (!this.waterAt(ax, this.y, az)) this.pick(ctx, 4);
    steerFlying(this.w, this.x, this.y, this.z, this.tx, this.ty, this.tz, 2, STEER);
    const sp = this.cruise * (this.panic > 0 ? 3 : 1);
    this.vx += (STEER.dx * sp - this.vx) * 0.15;
    this.vz += (STEER.dz * sp - this.vz) * 0.15;
    this.vy += (STEER.dy * sp - this.vy) * 0.15 + 0.02; // cancel water gravity
    this.faceToward(this.x + STEER.dx, this.z + STEER.dz);
    this.pitch = -Math.atan2(STEER.dy, Math.hypot(STEER.dx, STEER.dz) || 1) * 0.5;
  }
}

export class Squid extends WaterMob {
  protected cruise = 0.05;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'squid', x, y, z, { width: 0.95, height: 0.95, eye: 0.4, health: 10, speed: 0.1 }, rnd);
    this.noFallDamage = true;
  }
  protected ai(ctx: MobCtx): void {
    if (this.panic === 59) this.host?.effect('poof', this.x, this.y + 0.4, this.z); // ink cloud
    this.swim(ctx);
  }
  xpValue(rnd?: () => number): number { return roll(rnd ?? Math.random, 1, 3); }
  loot(rnd: () => number): Stack[] { return [stackOf(I.INK_SAC, roll(rnd, 1, 3))]; }
}

class Fish extends WaterMob {
  protected cruise = 0.08;
  protected readonly raw: number;
  protected readonly cooked: number;
  constructor(id: number, type: MobType, x: number, y: number, z: number, rnd: () => number, w: number, h: number, raw: number, cooked: number) {
    super(id, type, x, y, z, { width: w, height: h, eye: h * 0.5, health: 3, speed: 0.12 }, rnd);
    this.raw = raw; this.cooked = cooked;
    this.noFallDamage = true;
  }
  protected ai(ctx: MobCtx): void { this.swim(ctx); }
  xpValue(rnd?: () => number): number { return roll(rnd ?? Math.random, 1, 3); }
  loot(): Stack[] { return [stackOf(this.fireTicks > 0 ? this.cooked : this.raw, 1)]; }
}

export class Cod extends Fish {
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) { super(id, 'cod', x, y, z, rnd, 0.5, 0.3, I.COD, I.COOKED_COD); }
}
export class Salmon extends Fish {
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) { super(id, 'salmon', x, y, z, rnd, 0.7, 0.4, I.SALMON, I.COOKED_SALMON); }
}

// --------------------------------------------------------------------- bat

export class Bat extends Mob {
  /** Hanging from the ceiling (mirrored to view.color 1 for the renderer). */
  hanging = false;
  private tx = 0; private ty = 0; private tz = 0;
  private retarget = 0;

  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'bat', x, y, z, { width: 0.5, height: 0.9, eye: 0.45, health: 6, speed: 0.3 }, rnd);
    this.noFallDamage = true;
    this.hanging = rnd() < 0.5;
  }

  protected override floats(): boolean { return false; }
  override onHurtBy(): void { this.hanging = false; }
  xpValue(): number { return 0; }
  loot(): Stack[] { return []; }

  private solidAbove(): boolean {
    return this.w.getBlock(Math.floor(this.x), Math.floor(this.y + this.height + 0.1), Math.floor(this.z)) !== 0;
  }

  protected ai(ctx: MobCtx): void {
    const r = ctx.host.rnd;
    if (this.hanging) {
      if (!this.solidAbove()) this.hanging = false;
      else {
        this.vx = this.vy = this.vz = 0;
        this.vy = 0.08 * 0.98; // cancel gravity
        const p = ctx.host.player;
        // vanilla: wakes when a player is within 4 blocks, and at random (1/200)
        if ((p.x - this.x) ** 2 + (p.y - this.y) ** 2 + (p.z - this.z) ** 2 < 16 || r() < 0.005) this.hanging = false;
        return;
      }
    }
    if (--this.retarget <= 0 || (this.tx - this.x) ** 2 + (this.ty - this.y) ** 2 + (this.tz - this.z) ** 2 < 4) {
      this.retarget = 20 + Math.floor(r() * 40);
      this.tx = this.x + (r() * 2 - 1) * 7; this.ty = this.y + (r() * 2 - 2) * 3 + 1; this.tz = this.z + (r() * 2 - 1) * 7;
      this.ty = Math.max(this.ty, 2);
    }
    steerFlying(this.w, this.x, this.y, this.z, this.tx, this.ty, this.tz, 3, STEER);
    const sp = 0.1;
    this.vx += (STEER.dx * sp - this.vx) * 0.1;
    this.vz += (STEER.dz * sp - this.vz) * 0.1;
    this.vy += (STEER.dy * sp - this.vy) * 0.1 + 0.08 * 0.98;
    this.faceToward(this.x + this.vx, this.z + this.vz);
    // roost on a ceiling when it is above and nothing is near (daylight or 1/20 chance per retarget)
    if (this.solidAbove() && r() < 0.01) this.hanging = true;
  }

  override syncView(host: Parameters<Mob['syncView']>[0]): void {
    super.syncView(host);
    this.view.color = this.hanging ? 1 : 0;
  }

  saveExtra(): unknown { return [this.hanging ? 1 : 0]; }
  loadExtra(d: unknown): void { this.hanging = (d as number[])?.[0] === 1; }
}

/** Spawn-group sizes. */
export const GROUP: Record<'squid' | 'cod' | 'salmon' | 'bat', [number, number]> = { squid: [1, 4], cod: [3, 9], salmon: [1, 5], bat: [1, 3] };
